import moment from "moment";
import { useTheme } from "@material-ui/core/styles";

// Validated categorical palette (fixed order, never cycled), stepped per mode.
// Slot order is what keeps adjacent series apart for colour-blind readers, so
// assign slots by entity, never by rank.
const SERIES = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500"],
};

export const useChartTheme = () => {
  const theme = useTheme();
  const mode = theme.palette.type === "dark" ? "dark" : "light";
  return {
    series: SERIES[mode],
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    grid: theme.palette.divider,
    surface: theme.palette.background.paper,
  };
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const wholeCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat("en-US");

export const formatCurrency = (value) => currencyFormatter.format(value || 0);
export const formatWholeCurrency = (value) =>
  wholeCurrencyFormatter.format(value || 0);
export const formatCompactCurrency = (value) =>
  compactCurrencyFormatter.format(value || 0);
export const formatNumber = (value) => numberFormatter.format(value || 0);

// Percent change vs the previous period. null when there is nothing to
// compare against, so the UI can say "no prior data" instead of "+∞%".
export const percentChange = (current, previous) => {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

export const formatPeriod = (period, granularity, { long = false } = {}) => {
  const m = moment(period, "YYYY-MM-DD");
  if (granularity === "month") return m.format(long ? "MMMM YYYY" : "MMM YY");
  if (granularity === "week")
    return long ? `Week of ${m.format("MMM D, YYYY")}` : m.format("MMM D");
  return m.format(long ? "ddd, MMM D, YYYY" : "MMM D");
};

export const formatDate = (date) =>
  date ? moment(date, "YYYY-MM-DD").format("MMM D, YYYY") : "";

export const DATE_FORMAT = "YYYY-MM-DD";

export const RANGE_PRESETS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "Year to date" },
  { key: "12m", label: "Last 12 months" },
  { key: "custom", label: "Custom range" },
];

export const presetRange = (key) => {
  const today = moment().startOf("day");
  switch (key) {
    case "7d":
      return { from: today.clone().subtract(6, "days"), to: today };
    case "30d":
      return { from: today.clone().subtract(29, "days"), to: today };
    case "90d":
      return { from: today.clone().subtract(89, "days"), to: today };
    case "month":
      return { from: today.clone().startOf("month"), to: today };
    case "last_month": {
      const last = today.clone().subtract(1, "month");
      return {
        from: last.clone().startOf("month"),
        to: last.clone().endOf("month").startOf("day"),
      };
    }
    case "ytd":
      return { from: today.clone().startOf("year"), to: today };
    case "12m":
    default:
      return {
        from: today.clone().subtract(1, "year").add(1, "day"),
        to: today,
      };
  }
};
